import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Copy, ShareNetwork, Heart, Check, ArrowLeft } from "@phosphor-icons/react";
import Nav from "@/components/site/Nav";
import ShareBar from "@/components/site/ShareBar";
import { toast } from "sonner";

/**
 * /share — landing page for the "Copy my share message" button in
 * confirmation emails. Email HTML can't run JS, so we link here and provide
 * a real one-tap copy button. Auto-fills the "moving for" name from ?name=
 * query param and lets participants edit it in-place before copying.
 */
export default function Share() {
  const [params] = useSearchParams();
  const initialName = (params.get("name") || "").trim();
  const [name, setName] = useState(initialName);
  const [copied, setCopied] = useState(false);

  useEffect(() => { setName(initialName); }, [initialName]);

  const site = typeof window !== "undefined" ? window.location.origin : "https://sweatwithslay.com";
  const challengeUrl = `${site}/sweat-for-sacrifice`;

  const message = useMemo(() => {
    const filled = name.trim() || "___________";
    return (
      `I'm moving for ${filled}.\n\n` +
      "I'm taking on 911 Minutes of Movement as part of the Sweat for Sacrifice Legacy Challenge. " +
      "Join me in honoring the names, stories, and sacrifices we carry with us.\n\n" +
      `${challengeUrl}\n\n` +
      "#LegacyChallenge #SweatForSacrifice #SweatWithSlay #911MinutesOfMovement"
    );
  }, [name, challengeUrl]);

  const copyMessage = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(message);
      } else {
        // Fallback for older browsers
        const ta = document.createElement("textarea");
        ta.value = message; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Share message copied — paste it anywhere");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Copy failed — long-press the box below and copy manually");
    }
  };

  return (
    <main data-testid="share-page" className="min-h-screen bg-black text-white">
      <Nav />
      <section className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-24">
        <Link to="/sweat-for-sacrifice" data-testid="share-back-link" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-[11px] uppercase tracking-[0.3em] font-bold mb-8">
          <ArrowLeft size={12} weight="bold" /> Back to the Challenge
        </Link>
        <p className="text-[#B32027] uppercase tracking-[0.35em] text-xs font-bold mb-4 flex items-center gap-2">
          <ShareNetwork size={12} weight="fill" /> / Share the Challenge
        </p>
        <h1 className="font-display font-black uppercase leading-[0.95] tracking-tight text-5xl md:text-7xl">Who are you moving for?</h1>
        <p className="text-white/75 text-base md:text-lg leading-relaxed mt-4 max-w-2xl">
          Post it. Text it. Rally your people. Every share pulls another name onto the Legacy Wall.
        </p>

        <div className="mt-10 space-y-6">
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.3em] font-bold text-white/70 mb-2 block">Fill in the name</span>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Uncle Timmy"
              data-testid="share-name-input"
              className="w-full bg-black border-2 border-white/30 focus:border-[#B32027] text-white px-4 py-3 text-lg font-semibold outline-none"
            />
          </label>

          <div className="bg-[#0F0F0F] border-2 border-[#B32027] p-5 md:p-6">
            <p className="text-white text-lg md:text-xl leading-relaxed font-semibold mb-3">
              &ldquo;I&rsquo;m moving for <span className="text-[#B32027]" data-testid="share-preview-name">{name.trim() || "___________"}</span>.&rdquo;
            </p>
            <p className="text-white/75 text-sm md:text-base leading-relaxed mb-3">
              I&rsquo;m taking on 911 Minutes of Movement as part of the Sweat for Sacrifice Legacy Challenge. Join me in honoring the names, stories, and sacrifices we carry with us.
            </p>
            <p className="text-white/50 text-xs md:text-sm">{challengeUrl}</p>
            <p className="text-[#B32027] text-xs md:text-sm font-mono mt-2">#LegacyChallenge · #SweatForSacrifice · #SweatWithSlay · #911MinutesOfMovement</p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={copyMessage}
              data-testid="share-copy-btn"
              className="inline-flex items-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white px-6 py-4 text-[11px] font-bold uppercase tracking-[0.3em] transition-colors"
            >
              {copied ? <><Check size={14} weight="bold" /> Copied</> : <><Copy size={14} weight="bold" /> Copy my share message</>}
            </button>
            <Link
              to="/sweat-for-sacrifice"
              data-testid="share-challenge-link"
              className="inline-flex items-center gap-2 border-2 border-white hover:bg-white hover:text-black text-white px-6 py-4 text-[11px] font-bold uppercase tracking-[0.3em] transition-colors"
            >
              <Heart size={14} weight="fill" /> Legacy Challenge page
            </Link>
          </div>

          <div className="pt-6 border-t border-white/10">
            <p className="text-[11px] uppercase tracking-[0.3em] font-bold text-white/60 mb-3">One-tap share</p>
            <ShareBar shareUrl={challengeUrl} shareText={message} />
            <p className="text-white/50 text-xs mt-4 leading-relaxed">
              Instagram &amp; TikTok don&rsquo;t accept pre-filled captions — tap <strong className="text-white">Copy my share message</strong>, then paste it into your story or post.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
